import templateUrl from "/templates/footer.html?url";
import { clamp, nearlyEqual } from "$lib/core/js/util/math";
import { loadRemoteElement, getDefaultElementTargetSelector } from "../core";
import { Logger } from "$lib/core/js/util/logger";

export class Footer {
	/** @type {string} */
	#selector = getDefaultElementTargetSelector("Footer", true);
	/** @type {HTMLElement?} */
	#target = null;
	/** @type {string} */
	#defaultTab = "genInfo";
	/** @type {Logger} */
	#logger = new Logger("Footer");

  /** @type {HTMLElement?} */
  tabsMenu = null;
  /** @type {HTMLElement?} */
  footerContainer = null;
  /** @type {HTMLElement?} */
  panelsContainer = null;
  /** @type {HTMLElement?} */
  tabsSlideContainer = null;
  /** @type {HTMLElement?} */
  leftScrollButton = null;
  /** @type {HTMLElement?} */
  rightScrollButton = null;
  
  /** @type {IntersectionObserver?} */
  leftScrollButtonObserver = null;
  /** @type {boolean} */
  showLeftScrollButton = false;
  /** @type {IntersectionObserver?} */
  rightScrollButtonObserver = null;
  /** @type {boolean} */
  showRightScrollButton = false;

	/** @type {HTMLButtonElement|null} */
	selectedTab = null;
	
  // Attribute Bindings
  
  /** @type {number} */
  static scrollFactor = 0.8;
  /** @type {number} */
  windowWidth = 0;

	/**
	 * @param {{ selector?: string, target?: HTMLElement }} options
	 */
	constructor(options = {}) {
		this.#selector = options.selector ?? this.#selector;
		this.#target = options.target ?? null;
	}

	/**
	 * @returns {string}
	 */
	get selector() {
		return this.#selector;
	}

	/**
	 * @returns {HTMLElement?}
	 */
	get target() {
		if (!this.#target) {
			this.#target = document.querySelector(this.#selector);
		}
		return this.#target;
	}

	/**
	 * @returns {NodeListOf<HTMLButtonElement>}
	 */
	tabs() {
		return document.querySelectorAll(`${this.#selector} .tabs button`);
	}

	/**
	 * 
	 * @param {string} id 
	 */
	selectTab(id) {
		if (id.startsWith("#")) {
			id = id.slice(1);
		}

		for (let el of this.tabs()) {
			const ariaControls = el.getAttribute("aria-controls");
			const active = ariaControls == id;
			el.setAttribute("aria-selected", active ? "true" : "false");
			el.classList.toggle("active", active);
			if (active) {
				this.selectedTab = el;
			}

			const panel = this.panel(el);
			panel?.setAttribute("aria-hidden", active ? "false" : "true");
		}
	}

	selectedTabIndex() {
		let i = 0;
		for (let el of this.tabs()) {
			if (el.getAttribute("aria-selected") === "true") return i;
			i++;
		}
		this.#logger.warn(`Footer.selectedTabIndex: No footer tab selected!`);
		return -1;
	}

	/**
	 * Returns the tab panel associated with the tab.
	 * 
	 * @param {string|HTMLButtonElement} value 
	 * @returns {HTMLDivElement?}
	 */
	panel(value) {
		if (typeof(value) === "string" && value) {
			if (!value.startsWith("#")) {
				value = `#${value}`;
			}
			return document.querySelector(value);
		} else if (value instanceof HTMLButtonElement) {
			return this.panel(value.getAttribute("aria-controls") ?? "");
		}
		return null;
	}

	scrollAmount() {
		return this.windowWidth * Footer.scrollFactor;
	}

  /**
   * @param {number} offset 
   * @returns {HTMLButtonElement}
   */
  shiftTab(offset) {
		let tabs = [...this.tabs()];
    let pos = this.selectedTabIndex() + offset
    let min = 0;
    let max = tabs.length - 1;
    let i = clamp(pos, min, max);
    return tabs[i];
  }
  
  /**
   * Adds keyboard accessibility to the footer tabs.
   * @param {KeyboardEvent} ev
   */
  async onTabButtonKeyDown(ev) {
    
    let button = /** @type {HTMLElement} */ (ev.currentTarget);
    
    if (ev.code === "Enter") {
      button.click();
    } else if (ev.code === "ArrowLeft") {
      this.selectedTab = this.shiftTab(-1);
    } else if (ev.code === "ArrowRight") {
      this.selectedTab = this.shiftTab(1);
    }
    
    this.selectedTab?.focus();
  }

	/**
	 * Asynchronously loads the element from a centrally maintained template. Does NOT initialize it.
	 * Recommended that you call `init()` after this Promise completes.
	 * 
	 * @param {string?} [url=null] 
	 * @returns {Promise<void>}
	 */
	async load(url = null) {
		url = url ? url : templateUrl;
		try {
			this.#target = await loadRemoteElement({
				type: "Footer",
				root: true,
				target: this.target,
				url,
				templateId: "bu-footer",
			});
			// This is done for compatibility reasons. Many apps already have the `footer` class on their `<footer data-is="Footer"></footer>` element.
			// To avoid having to redeploy/fix all of those while also allowing extra classes to be attached to the remote-loaded content,
			// we are adding logic here to remove any "footer" class present if the child already has it.
			if (this.#target != null && this.#target.classList.contains("footer") && this.#target.firstElementChild?.classList.contains("footer")) {
				this.#target.classList.remove("footer");
			}
		} catch (err) {
			console.error(err);
		}
	}

	/**
	 * Initializes the footer's state & attaches any necessary event handlers.
	 * 
	 * @returns {void}
	 */
	init() {
		this.#logger.debug("init() start");
		const footer = this.target;
		if (!footer) {
			this.#logger.warn("No footer detected!");
			return;
		}
		this.#logger.debug("Footer target", footer);

		this.tabsMenu = this.target.querySelector(":scope .tabs");
		this.tabsSlideContainer = this.tabsMenu?.parentElement ?? null;
		this.leftScrollButton = document.getElementById("footer-left-scroll");
		this.rightScrollButton = document.getElementById("footer-right-scroll");

		// Setup "change" event for `footer`.
		for (let el of this.tabs()) {
			const id = el.getAttribute("aria-controls");
			const event = (/** @type {Event} */ ev) => new CustomEvent("change", { detail: { event: ev, value: id }});
			const handler = (/** @type {MouseEvent|KeyboardEvent} */ ev) => { footer.dispatchEvent(event(ev)); this.#logger.debug(`Tab '${id}' triggering event ${ev.type}`); }
			el.addEventListener("click", handler);
			el.addEventListener("keypress", handler);
			this.#logger.debug(`Tab Button handlers for 'click' and 'keypress' attached, targeting id '${id}'.`);
		}

    const threshold = [1];
		const footerTabs = [...this.tabs()];
    
    let leftMostTab = footerTabs[0];
    if (leftMostTab) {
      this.leftScrollButtonObserver = new IntersectionObserver(this.#horizontalScrollIntersectionHandler(true), { root: this.target, threshold });
      this.leftScrollButtonObserver.observe(leftMostTab);
    }
    
    let rightMostTab = footerTabs[footerTabs.length - 1];
    if (rightMostTab) {
      this.rightScrollButtonObserver = new IntersectionObserver(this.#horizontalScrollIntersectionHandler(false), { root: this.target, threshold });
      this.rightScrollButtonObserver.observe(rightMostTab);
    }

		this.#logger.debug("Key elements", {
			tabs: footerTabs,
			tabsMenu: this.tabsMenu,
			tabsSlideContainer: this.tabsSlideContainer,
			leftScrollButton: this.leftScrollButton,
			rightScrollButton: this.rightScrollButton,
		});

		this.windowWidth = window.innerWidth;
		window.addEventListener("resize", () => {
			this.windowWidth = window.innerWidth;
		});

		this.target.addEventListener("change", (/** @type {Event} */ ev) => {
			if (ev instanceof CustomEvent) {
				this.selectTab(ev.detail.value);
			}
		});

		this.leftScrollButton?.addEventListener("click", this.scrollButtonClickHandler(true).bind(this));
		this.rightScrollButton?.addEventListener("click", this.scrollButtonClickHandler(false).bind(this));

		// Init `footer` state.
		this.selectTab(this.#defaultTab);

		this.#logger.debug("init() end");
	}

  /**
   * @param {boolean} isLeftScrollButton
	 * @returns {(ev: MouseEvent) => void}
   */
  scrollButtonClickHandler(isLeftScrollButton) {
		return (ev) => {
			ev.preventDefault();
			if (this.tabsSlideContainer) {
				if (isLeftScrollButton) {
					this.tabsSlideContainer.scrollLeft -= this.scrollAmount();
				} else {
					this.tabsSlideContainer.scrollLeft += this.scrollAmount();
				}
			}
		}
	}

	/**
	 * @param {boolean} isLeftTab
	 */
	#horizontalScrollIntersectionHandler(isLeftTab) {
			return (/** @type {IntersectionObserverEntry[]} */ entries) => {
			let entry = entries[0];
			let show = !entry.isIntersecting && nearlyEqual(entry.intersectionRatio, 0)
				|| !entry.isIntersecting && nearlyEqual(entry.intersectionRatio, 1);
			if (isLeftTab) {
				if (this.leftScrollButton) {
					this.leftScrollButton.classList.toggle("hidden", !show);
					this.leftScrollButton.ariaHidden = show ? "false" : "true";
				}
			} else {
				if (this.rightScrollButton) {
					this.rightScrollButton.classList.toggle("hidden", !show);
					this.rightScrollButton.ariaHidden = show ? "false" : "true";
				}
			}
		}
	}
}

export default Footer;
